import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const SurveyResults = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [stats, setStats] = useState(null);
  const [responses, setResponses] = useState(null);
  const [responsesLoading, setResponsesLoading] = useState(false);

  const ensureAuthHeader = () => {
    const token = localStorage.getItem('token');
    if (token && !axios.defaults.headers.common['Authorization']) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  };

  const fetchSurveys = useCallback(async () => {
    ensureAuthHeader();
    setLoading(true);
    try {
      const response = await axios.get('/api/surveys');
      setSurveys(response.data.surveys);
      if (response.data.surveys.length > 0 && !selectedId) {
        const first = response.data.surveys[0];
        setSelectedId(first._id);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load surveys');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  const selectedSurvey = surveys.find((s) => s._id === selectedId) || null;

  const fetchDetails = useCallback(async (id) => {
    ensureAuthHeader();
    setResponsesLoading(true);
    setStats(null);
    setResponses(null);
    try {
      const [statsRes, responsesRes] = await Promise.all([
        axios.get(`/api/surveys/${id}/stats`),
        axios.get(`/api/surveys/${id}/responses?limit=500`)
      ]);
      setStats(statsRes.data);
      setResponses(responsesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load survey results');
    } finally {
      setResponsesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) {
      fetchDetails(selectedId);
    }
  }, [selectedId, fetchDetails]);

  const updateStatus = async (survey, changes) => {
    ensureAuthHeader();
    try {
      await axios.patch(`/api/surveys/${survey._id}/status`, changes);
      toast.success(changes.isActive ? 'Survey activated' : changes.archived ? 'Survey archived' : 'Survey deactivated');
      fetchSurveys();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not update survey');
    }
  };

  const copyLink = (slug) => {
    if (!slug) return;
    const url = `${window.location.origin}/survey/${slug}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success('Survey link copied to clipboard'))
      .catch(() => toast.error('Could not copy link'));
  };

  const handleDelete = async (survey) => {
    ensureAuthHeader();
    if (!window.confirm(`Delete "${survey.title}" and all ${survey.responseCount} responses? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`/api/surveys/${survey._id}`);
      toast.success('Survey deleted');
      setSelectedId(null);
      setStats(null);
      setResponses(null);
      fetchSurveys();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not delete survey');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const answerValue = (response, questionId) => {
    const answer = response.answers.find((a) => a.questionId === questionId);
    if (!answer) return '—';
    if (Array.isArray(answer.value)) return answer.value.join(', ');
    return String(answer.value);
  };

  const StatBars = ({ stat }) => {
    if (stat.type === 'text') {
      return (
        <div className="space-y-1 mt-2">
          {stat.answers && stat.answers.length > 0 ? (
            stat.answers.map((text, i) => (
              <p key={i} className="text-sm text-secondary bg-tertiary rounded px-3 py-2">
                {text}
              </p>
            ))
          ) : (
            <p className="text-sm text-muted">No text responses</p>
          )}
        </div>
      );
    }

    if (stat.type === 'rating') {
      const total = Object.values(stat.counts).reduce((sum, v) => sum + v, 0);
      return (
        <div className="mt-2 space-y-1">
          {[5, 4, 3, 2, 1].map((num) => (
            <div key={num} className="flex items-center gap-2">
              <span className="w-4 text-sm text-tertiary">{num}★</span>
              <div className="flex-1 bg-tertiary rounded-full h-4 overflow-hidden">
                <div
                  className="h-full bg-indigo-500 rounded-full"
                  style={{ width: total > 0 ? `${(stat.counts[num] / total) * 100}%` : '0%' }}
                />
              </div>
              <span className="w-8 text-xs text-tertiary">{stat.counts[num]}</span>
            </div>
          ))}
          <p className="text-sm text-secondary pt-1">
            Average: <strong>{stat.average ?? '—'}</strong> / 5
          </p>
        </div>
      );
    }

    const entries = Object.entries(stat.counts).filter(([, v]) => v > 0);
    const max = Math.max(...Object.values(stat.counts), 1);
    return (
      <div className="mt-2 space-y-1">
        {entries.length > 0 ? (
          entries.map(([option, count]) => (
            <div key={option} className="flex items-center gap-2">
              <span className="w-40 text-sm text-secondary truncate" title={option}>{option}</span>
              <div className="flex-1 bg-tertiary rounded-full h-4 overflow-hidden">
                <div
                  className="h-full bg-indigo-500 rounded-full"
                  style={{ width: `${(count / max) * 100}%` }}
                />
              </div>
              <span className="w-8 text-xs text-tertiary">{count}</span>
            </div>
          ))
        ) : (
          <p className="text-sm text-muted">No responses yet</p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Surveys</h1>
        {isAdmin && (
          <button
            onClick={() => navigate('/admin/surveys/new')}
            className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
            New Survey
          </button>
        )}
      </div>

      {surveys.length === 0 ? (
        <div className="bg-card rounded-xl shadow p-8 text-center">
          <p className="text-secondary">No surveys yet. Create your first one!</p>
        </div>
      ) : (
        <>
          {/* Survey list */}
          <div className="bg-card rounded-xl shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-tertiary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-tertiary uppercase tracking-wider">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-tertiary uppercase tracking-wider">Responses</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-tertiary uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-tertiary uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {surveys.map((survey) => (
                  <tr
                    key={survey._id}
                    onClick={() => setSelectedId(survey._id)}
                    className={`cursor-pointer transition-colors ${selectedId === survey._id ? 'bg-indigo-50 dark:bg-indigo-500/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                  >
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-primary">{survey.title}</div>
                      <div className="text-xs text-tertiary">/{survey.slug}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary">{survey.responseCount}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {survey.isActive && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-500/10 dark:text-green-400">
                            Live
                          </span>
                        )}
                        {survey.archived && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-tertiary text-secondary">
                            Archived
                          </span>
                        )}
                        {!survey.isActive && !survey.archived && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-500/10 dark:text-yellow-400">
                            Draft
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-wrap gap-2">
                        {isAdmin && (
                          <button
                            onClick={() => navigate(`/admin/surveys/${survey._id}/edit`)}
                            className="text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                          >
                            Edit
                          </button>
                        )}
                        <button
                          onClick={() => copyLink(survey.slug)}
                          className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          Copy link
                        </button>
                        {isAdmin && (
                          <>
                            {survey.isActive ? (
                              <button
                                onClick={() => updateStatus(survey, { isActive: false })}
                                className="text-sm text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-300"
                              >
                                Deactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => updateStatus(survey, { isActive: true })}
                                className="text-sm text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                              >
                                Activate
                              </button>
                            )}
                            {survey.archived ? (
                              <button
                                onClick={() => updateStatus(survey, { archived: false })}
                                className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                Unarchive
                              </button>
                            ) : (
                              <button
                                onClick={() => updateStatus(survey, { archived: true })}
                                className="text-sm text-tertiary hover:text-secondary"
                              >
                                Archive
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(survey)}
                              className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Survey details */}
          {selectedSurvey && (
            <div className="space-y-6">
              <div className="bg-card rounded-xl shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-primary">{selectedSurvey.title}</h2>
                  <span className="text-sm text-tertiary">
                    {stats?.totalResponses ?? 0} total responses
                  </span>
                </div>

                {responsesLoading ? (
                  <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                  </div>
                ) : stats && stats.stats.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {stats.stats.map((stat) => (
                      <div key={stat.questionId} className="bg-tertiary rounded-lg p-4">
                        <p className="text-sm font-medium text-primary">{stat.label}</p>
                        <StatBars stat={stat} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-tertiary">No responses yet.</p>
                )}
              </div>

              {/* Responses table */}
              {responses && responses.responses && responses.responses.length > 0 && (
                <div className="bg-card rounded-xl shadow overflow-hidden">
                  <div className="px-6 py-4 border-b border-primary">
                    <h2 className="text-lg font-semibold text-primary">Individual Responses</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-tertiary">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-tertiary uppercase tracking-wider">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-tertiary uppercase tracking-wider">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-tertiary uppercase tracking-wider">Email</th>
                          {selectedSurvey.questions.map((q) => (
                            <th key={q.id} className="px-4 py-3 text-left text-xs font-medium text-tertiary uppercase tracking-wider">
                              {q.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {responses.responses.map((response) => (
                          <tr key={response._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-4 py-3 text-sm text-secondary whitespace-nowrap">{formatDate(response.createdAt)}</td>
                            <td className="px-4 py-3 text-sm text-secondary">{response.responder?.name || '—'}</td>
                            <td className="px-4 py-3 text-sm text-secondary">{response.responder?.email || '—'}</td>
                            {selectedSurvey.questions.map((q) => (
                              <td key={q.id} className="px-4 py-3 text-sm text-secondary max-w-xs truncate" title={answerValue(response, q.id)}>
                                {answerValue(response, q.id)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SurveyResults;